import React, { Component, Fragment } from "react"
import { connect } from "react-redux"
import { compose } from "redux"
import PropTypes from "prop-types"
import { translate } from "react-i18next"
import uniqid from "uniqid"
import styled from "styled-components"
import { lighten } from "polished"
import { Scrollbars } from "react-custom-scrollbars"

const UnselectedCheckboxLabel = styled.label`
  font-size: 14px !important;
  color: #777 !important;
  margin: 2px 0 !important;
  font-weight: normal !important;
  &:after {
    border-color: ${props => lighten(0.3, props.primaryColor)} !important;
    background-color: ${props => lighten(0.5, props.primaryColor)} !important;
  }
  &:hover:after {
    background-color: ${props => lighten(0.3, props.primaryColor)} !important;
  }
`

const SelectedCheckboxLabel = UnselectedCheckboxLabel.extend`
  &:after {
    background-color: ${props => lighten(0.1, props.primaryColor)} !important;
  }
`

const ClearSelectionButton = styled.a`
  margin: 2px 0;
  color: ${props => lighten(0.2, props.primaryColor)} !important;

  &:hover {
    color: ${props => props.primaryColor} !important;
    cursor: pointer;
  }
`
const Input = styled.input`
  width: 90%;
  line-height: 1em;
  margin-bottom: 20px;
  min-height: 2em;
  background: #fff;
  display: inline-block;
  padding: 7px;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-bottom-left-radius: 0.3rem;
  border-top-left-radius: 0.3rem;
`
const InputIcon = styled.span`
  width: 10%;
  margin-bottom: 20px;
  min-height: 2em;
  background: #fff;
  display: inline-block;
  padding: 7px;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-bottom-right-radius: 0.3rem;
  border-top-right-radius: 0.3rem;
  border-left: none;
  background-color: #eeeeee;
`
class CheckboxList extends Component {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        selected: PropTypes.bool.isRequired
      })
    ).isRequired,
    scrollTop: PropTypes.number,
    sortAlphabetically: PropTypes.bool,
    translateLabels: PropTypes.bool,
    verticalScrollSize: PropTypes.number,
    theme: PropTypes.shape({
      primaryColor: PropTypes.string.isRequired,
      primaryColorHover: PropTypes.string.isRequired,
      navBackgroundColor: PropTypes.string.isRequired
    }).isRequired,
    includeSearch: PropTypes.bool
  }

  static defaultProps = {
    scrollTop: 0,
    sortAlphabetically: false,
    translateLabels: false,
    verticalScrollSize: 100,
    includeSearch: false
  }

  constructor(props) {
    super(props)
    this.state = {
      search: ""
    }
  }
  componentWillMount = () => {
    this.controlId = `checks_${uniqid()}`
  }

  componentDidMount = () => {
    // Set the scroll position. Preserves scroll position on re-render
    // which happens as the user ticks checkboxes
    this.scrollbarsRef.scrollTop(this.props.scrollTop)
  }

  updateSearch = event => {
    this.setState({ search: event.target.value })
  }

  clearSelections = () => {
    this.props.onChange([], null, 0)
  }

  handleCheckboxChange = e => {
    // Create a copy of the options prop
    let values = [...this.props.options]

    // Change the value for the target whose value change
    // invoked this function
    values = values.map(opt => {
      if (opt.value === e.target.value) {
        return { ...opt, selected: e.target.checked }
      }
      return opt
    })

    // Filter out the unselected options
    values = values.filter(opt => opt.selected)

    // Get the text to display when this field is collapsed,
    // e.g. "Option A & 3 more"
    let textToDisplay = null
    if (values.length > 0) {
      textToDisplay = this.props.t(values[0].label)

      if (values.length > 1) {
        const remainingCount = values.length - 1
        // eslint-disable-next-line react/prop-types
        textToDisplay = `${textToDisplay} + ${remainingCount} ${this.props.t("Common.more")}`
      }
    }

    // Return just the values of the selected options
    values = values.map(opt => opt.value)

    // Get the position of the scrollbar. Without this, the scroll
    // position will reset to the top of the list on re-render
    const scrollTop = this.scrollbarsRef.getScrollTop()

    this.props.onChange(values, textToDisplay, scrollTop)
  }

  render() {
    const {
      t,
      options,
      translateLabels,
      verticalScrollSize,
      sortAlphabetically,
      includeSearch,
      theme
    } = this.props

    const selectedCount = options.filter(opt => opt.selected).length

    const groupName = `chks_${this.controlId}`

    let optionCollection = options.map(opt => {
      return { item: opt, text: translateLabels ? t(opt.label) : opt.label }
    })

    if (sortAlphabetically) {
      optionCollection = optionCollection.sort((a, b) => a.text.localeCompare(b.text))
    }

    // If the includeSearch property is true or if there is more than 9 options we want to allow the filtering,
    // also below you will see that the search text box only appears under the same condition.
    if (includeSearch || optionCollection.length > 9) {
      optionCollection = optionCollection.filter(option => {
        return option.text.toLowerCase().indexOf(this.state.search.toLowerCase()) !== -1
      })
    }

    const checkListItems = optionCollection.map(opt => {
      const checkboxId = `chk_${this.controlId}_${opt.item.value}`
      return (
        <div key={`${opt.item.value}_${opt.item.label}`}>
          <input
            type="checkbox"
            className="filled-in"
            id={checkboxId}
            name={groupName}
            value={opt.item.value}
            onChange={this.handleCheckboxChange}
            checked={opt.item.selected}
          />
          {opt.item.selected ? (
            <SelectedCheckboxLabel htmlFor={checkboxId} primaryColor={theme.primaryColor}>
              {opt.text}
            </SelectedCheckboxLabel>
          ) : (
            <UnselectedCheckboxLabel htmlFor={checkboxId} primaryColor={theme.primaryColor}>
              {opt.text}
            </UnselectedCheckboxLabel>
          )}
        </div>
      )
    })

    return (
      <Fragment>
        {this.props.includeSearch || optionCollection.length > 9 ? (
          <Fragment>
            <Input type="text" value={this.state.search} onChange={this.updateSearch} />
            <InputIcon>
              <i className="fa fa-search" />
            </InputIcon>

            <h6>
              {t("Options")} ({checkListItems.length})
            </h6>
          </Fragment>
        ) : null}
        <Scrollbars
          autoHeight
          autoHeightMin={20}
          autoHeightMax={verticalScrollSize}
          renderTrackHorizontal={props => (
            <div {...props} style={{ display: "none" }} className="track-horizontal" />
          )}
          ref={c => {
            this.scrollbarsRef = c
          }}
        >
          {checkListItems}
          {selectedCount > 0 &&
            options.length > 3 && (
              <ClearSelectionButton
                onClick={this.clearSelections}
                primaryColor={theme.primaryColor}
              >
                {t("Filters.ClearCheckChoices")}
              </ClearSelectionButton>
            )}
        </Scrollbars>
      </Fragment>
    )
  }
}

function mapStateToProps(state) {
  return {
    theme: state.userDetail.theme
  }
}

export default compose(translate(), connect(mapStateToProps))(CheckboxList)



// WEBPACK FOOTER //
// ./src/components/inputs/CheckboxList.jsx